<template>
    <div
        class="timeline-card"
        :style="cssVars">
        <div class="date-bullet">
            {{ timelineItemIn.date | date_written }}
        </div>
        <div class="date">
            {{ timelineItemIn.date | date_written }}
        </div>

        <div class="content">
            <div
                class="header"
                :class="{ 'no-media': !haveMedia }">
                <img
                    v-if="timelineItemIn.image_files.length !== 0 && haveMedia"
                    :src="timelineItemIn.image_files[0].file.public_path"
                    :alt="timelineItemIn.image_files[0].file.name" />

                <design-2021-apprentice-video-player-component
                    class="video"
                    v-else-if="
                        (timelineItemIn.video_files.length !== 0 || timelineItemIn.video_links.length !== 0) &&
                        haveMedia
                    "
                    :options="getVideoOptions()"
                    :height="125"
                    :key="timelineItemIn.id">
                </design-2021-apprentice-video-player-component>
            </div>

            <h4 class="title">{{ timelineItemIn.title }}</h4>
            <div
                class="category"
                v-if="timelineItemIn.category">
                {{ timelineItemIn.category.title }}
            </div>
            <design-2021-apprentice-display-html-component
                class="short-description"
                v-trim="20"
                :html-content="timelineItemIn.text">
            </design-2021-apprentice-display-html-component>
            <div class="btn-show-more btn-show-full-timeline">meer</div>
        </div>

        <a
            class="clickable"
            href=""
            @click.prevent.stop="viewProfileCard"></a>
    </div>
</template>

<script>
export default {
    name: 'design-2021-horizontal-timeline-card-component',
    props: {
        timelineItemIn: {
            type: Object,
            required: true,
        },

        userCredentialsIn: {
            type: Object,
            required: true,
        },

        teacherAccess: {
            type: Boolean,
            required: true,
        },
    },

    computed: {
        cssVars() {
            if (this.timelineItemIn.category) {
                return {
                    '--border-color': this.timelineItemIn.category.color,
                };
            }
        },
    },

    methods: {
        haveMedia() {
            return (
                this.timelineItemIn.image_files.length !== 0 ||
                this.timelineItemIn.video_files.length !== 0 ||
                this.timelineItemIn.video_links.length !== 0
            );
        },

        getVideoOptions() {
            let source = [];
            if (this.timelineItemIn.video_files.length !== 0) {
                source.push({
                    src: this.timelineItemIn.video_files[0].file
                        ? this.timelineItemIn.video_files[0].file.public_path
                        : '',
                    type: `video/${this.timelineItemIn.video_files[0].file.extension}`,
                });
            }

            if (this.timelineItemIn.video_links.length !== 0) {
                source.push({
                    src: this.timelineItemIn.video_links[0].url ? this.timelineItemIn.video_links[0].url : '',
                    type: 'video/youtube',
                });
            }

            return {
                autoplay: false,
                controls: true,
                sources: source,
                height: 125,
                fluid: false,
            };
        },

        viewProfileCard() {
            window.location.href = this.teacherAccess
                ? this.routes.profileTeacherAccessViewCard(this.userCredentialsIn.id, this.timelineItemIn.id)
                : this.routes.profileViewCard(this.timelineItemIn.id);
        },
    },
};
</script>

<style scoped lang="scss">
.timeline-card {
    position: relative;
    display: grid;
    grid-template-columns: 25px 1fr;
    background-color: var(--border-color);
    border-radius: $border-radius-content;
    margin-top: 115px;
    scroll-snap-align: center;

    .date-bullet {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        row-gap: 4px;
        top: -103px;
        left: 37px;
        color: $text-muted-color;
        font-weight: $font-weight-medium;
        height: auto;

        &:after {
            content: '';
            display: block;
            height: 30px;
            width: 30px;
            background-color: var(--border-color);
            border-radius: 50%;
        }
    }

    .date {
        grid-area: 1/1/1/2;
        color: $white;
        transform: rotate(-90deg);
        transform-origin: center;
        margin: auto 0;
        font-family: $headings-font-family;
        font-weight: $font-weight-medium;
        white-space: nowrap;
    }

    .content {
        grid-area: 1/2/1/3;
    }

    &:before {
        position: absolute;
        display: block;
        content: '';
        border-right: 1em solid transparent;
        border-left: 1em solid transparent;
        border-bottom: 1.3em solid var(--border-color);
        top: -18px;
        left: 43px;
    }

    &:after {
        position: absolute;
        display: block;
        content: '';
        border-radius: 50%;
        left: 43px;
        top: -50px;
        background-color: var(--border-color);
    }
}

.content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    background-color: $white;
    margin: 3px 3px 3px 0;
    border-top-right-radius: 9px;
    border-bottom-right-radius: 9px;
    padding-bottom: map-get($grid-gutter-spacing, 'ten');

    .header {
        height: 125px;
        width: 100%;
        margin-bottom: map-get($grid-gutter-spacing, 'ten');
        border-top-right-radius: 9px;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center;
            border-top-right-radius: 8px;
        }

        ::v-deep .video {
            &,
            * {
                border-top-right-radius: 9px;
            }
        }

        ::v-deep .video-js {
            border-top-right-radius: 9px;
        }

        &:has(+ .category),
        &:has(+ .short-description) {
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        }

        &:not(:empty) {
            z-index: 50;
        }

        &:empty {
            background-image: url('/images/no-media.jpg');
            background-repeat: no-repeat;
            background-position: center;
            background-size: cover;
        }
    }

    .category,
    .title,
    .short-description,
    .btn-show-full-timeline {
        padding: 0 map-get($grid-gutter-spacing, 'fifteen');
    }

    .category {
        color: $text-muted-color;
        font-size: 0.75rem;
        font-weight: $font-weight-medium;
    }

    .short-description {
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
    }

    .btn-show-full-timeline {
        align-self: flex-end;
        color: $primary;
        margin-top: auto;
    }
}
</style>
